import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import { ErrorMessage, RequiredAsterisk } from '@atlaskit/form';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import Link from '@atlaskit/link';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { type CsatRating } from '../../../../services/feedback/jsm-feedback';
import { isAtlassianEmployee } from '../../../utils/date-time-util/isAtlassianEmployee';
import { useGlobalConfig } from '../../../utils/global-config/useGlobalConfig';
import { isInternalUserWithAutoIncludeHistory } from '../../../utils/isInternalUserWithAutoIncludeHistory';
import { shouldShowOptInPrompt } from '../../../utils/shouldShowOptInPrompt';
import { useUIConfig } from '../../../utils/ui-config/useUIConfig';

import type { FeedbackType } from './FeedbackType';
import messages from './messages';

const leftAlignPadding = token('space.300');

const styles = cssMap({
	bannerContainer: {
		marginTop: token('space.200'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		inlineSize: '100%',
		maxInlineSize: '520px',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
	},
	categoryButtons: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.100'),
	},
	optInButton: {
		display: 'flex',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		overflow: 'hidden',
	},
	alignedWithText: {
		paddingLeft: leftAlignPadding,
	},
	textWithMaxWidth: {
		paddingLeft: leftAlignPadding,
		maxWidth: '90%',
	},
	subtleHeading: {
		font: token('font.heading.xsmall'),
	},
	textAreaWrap: {
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.input'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
		overflow: 'hidden',
		'&:focus-within': {
			borderColor: token('color.border.focused'),
			borderWidth: token('border.width.focused'),
		},
	},
});

type FeedbackConfirmationBannerProps = {
	onProvideAdditionalFeedback: () => void;
	onDismiss: () => void;
	isVisible: boolean;
	feedbackType: FeedbackType;
	onSubmit: (data: {
		categories: string[];
		additionalFeedback: string;
		includeMessages: boolean;
		enrollInResearch: boolean;
	}) => void;
	customCategories?: readonly string[];
	userEmail?: string;
	onIncludeMessagesChecked?: () => void;
	shouldHideCheckboxes?: boolean;
	storeUserMessages?: boolean;
	onOptIn?: () => void;
	isOptedIn?: boolean;
	hasSeenFeedbackOptInPrompt?: boolean;
	onOptInPromptActioned?: () => void;
	restartAutoDismiss?: () => void;
	pauseAutoDismiss?: () => void;
	csatRating?: CsatRating | null;
};

const CATEGORY_MESSAGE_MAP: Record<string, keyof typeof messages> = {
	upToDate: 'categoryUpToDate',
	accurate: 'categoryAccurate',
	helpful: 'categoryHelpful',
	onBrief: 'categoryOnBrief',
	wellSourced: 'categoryWellSourced',
	other: 'categoryOther',
	outOfDate: 'categoryOutOfDate',
	inaccurate: 'categoryInaccurate',
	tooLong: 'categoryTooLong',
	tooShort: 'categoryTooShort',
	poorlySourced: 'categoryPoorlySourced',
	inappropriate: 'categoryInappropriate',
	// Studio-specific categories
	goodPlan: 'categoryGoodPlan',
	askedGoodQuestions: 'categoryAskedGoodQuestions',
	reasonableAssumptions: 'categoryReasonableAssumptions',
	showedMeSomethingNew: 'categoryShowedMeSomethingNew',
	tooManyQuestions: 'categoryTooManyQuestions',
	notEnoughQuestions: 'categoryNotEnoughQuestions',
	incorrectPlan: 'categoryIncorrectPlan',
	buildFailed: 'categoryBuildFailed',
	tooWordy: 'categoryTooWordy',
	badAssumptions: 'categoryBadAssumptions',
};

const POSITIVE_CATEGORIES = [
	'upToDate',
	'accurate',
	'helpful',
	'onBrief',
	'wellSourced',
	'other',
] as const;

const NEGATIVE_CATEGORIES = [
	'outOfDate',
	'inaccurate',
	'tooLong',
	'tooShort',
	'poorlySourced',
	'inappropriate',
	'other',
] as const;

const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy';

type BannerHeaderProps = {
	messageKey: keyof typeof messages;
	onDismiss: () => void;
};

const BannerHeader = ({ messageKey, onDismiss }: BannerHeaderProps) => {
	const { formatMessage } = useIntl();
	return (
		<Inline space="space.100" alignBlock="center" spread="space-between">
			<Inline space="space.100" alignBlock="center">
				<CheckCircleIcon label="" color={token('color.icon.success')} />
				<Text color="color.text.subtle">{formatMessage(messages[messageKey])}</Text>
			</Inline>
			<IconButton
				appearance="subtle"
				icon={(props) => <CrossIcon size="small" {...props} />}
				label={formatMessage(messages.dismissBanner)}
				onClick={onDismiss}
				spacing="compact"
				isTooltipDisabled={false}
			/>
		</Inline>
	);
};

type ThankYouMessageProps = {
	onDismiss: () => void;
};

const ThankYouMessage = ({ onDismiss }: ThankYouMessageProps) => (
	<BannerHeader messageKey="thankYouMessage" onDismiss={onDismiss} />
);

type ThankYouOptInPromptProps = {
	onDismiss: () => void;
	onOptIn: () => void;
	onAdditionalFeedback: () => void;
};

const ThankYouOptInPrompt = ({
	onDismiss,
	onOptIn,
	onAdditionalFeedback,
}: ThankYouOptInPromptProps) => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.100">
			<BannerHeader messageKey="thankYouMessage" onDismiss={onDismiss} />
			<Box xcss={styles.textWithMaxWidth}>
				<Text size="medium" color="color.text.subtle">
					<FormattedMessage
						{...messages.optInQuestion}
						values={{
							b: (text) => (
								<Text as="span" weight="bold">
									{text}
								</Text>
							),
						}}
					/>
				</Text>
			</Box>
			<Box xcss={styles.alignedWithText}>
				<Inline space="space.100">
					<Box xcss={styles.optInButton}>
						<Button appearance="subtle" onClick={onOptIn}>
							{formatMessage(messages.yesOptIn)}
						</Button>
					</Box>
					<Box xcss={styles.optInButton}>
						<Button appearance="subtle" onClick={onAdditionalFeedback}>
							{formatMessage(messages.additionalFeedbackButton)}
						</Button>
					</Box>
				</Inline>
			</Box>
		</Stack>
	);
};

type OptedInMessageProps = {
	onDismiss: () => void;
	onAdditionalFeedback: () => void;
};

const OptedInMessage = ({ onDismiss, onAdditionalFeedback }: OptedInMessageProps) => {
	const { formatMessage } = useIntl();
	return (
		<Stack space="space.200">
			<BannerHeader messageKey="optedInMessage" onDismiss={onDismiss} />
			<Button onClick={onAdditionalFeedback} shouldFitContainer appearance="default">
				{formatMessage(messages.provideAdditionalFeedback)}
			</Button>
		</Stack>
	);
};

type ExpandedFeedbackFormProps = {
	feedbackType: FeedbackType;
	onDismiss: () => void;
	categories: readonly string[];
	selectedCategories: string[];
	toggleCategory: (category: string) => void;
	additionalFeedback: string;
	setAdditionalFeedback: (value: string) => void;
	includeMessages: boolean;
	setIncludeMessages: (value: boolean) => void;
	enrollInResearch: boolean;
	setEnrollInResearch: (value: boolean) => void;
	handleSubmit: () => void;
	showValidationError: boolean;
	clearValidationError: () => void;
	shouldHideCheckboxes?: boolean;
	isInternalUser: boolean;
	textAreaRef: React.RefObject<HTMLTextAreaElement>;
	headingRef?: React.RefObject<HTMLHeadingElement>;
	restartAutoDismiss?: () => void;
	pauseAutoDismiss?: () => void;
};

const ExpandedFeedbackForm = ({
	feedbackType,
	onDismiss,
	categories,
	selectedCategories,
	toggleCategory,
	additionalFeedback,
	setAdditionalFeedback,
	includeMessages,
	setIncludeMessages,
	enrollInResearch,
	setEnrollInResearch,
	handleSubmit,
	showValidationError,
	clearValidationError,
	shouldHideCheckboxes,
	isInternalUser,
	textAreaRef,
	headingRef,
	restartAutoDismiss,
	pauseAutoDismiss,
}: ExpandedFeedbackFormProps) => {
	const { formatMessage } = useIntl();
	const errorMessageId = useId();
	const isOtherSelected = selectedCategories.includes('other');
	const showError = showValidationError && isOtherSelected && !additionalFeedback.trim();

	// Pause auto-dismiss while pointer OR keyboard focus is inside the form;
	// resume only when both have left. The `relatedTarget` guard on
	// `mouseout` / `blur` is required because those events bubble and fire on
	// every internal boundary crossing.
	const isHoveredRef = useRef(false);
	const isFocusedRef = useRef(false);
	const resumeIfIdle = useCallback(() => {
		if (!isHoveredRef.current && !isFocusedRef.current) {
			restartAutoDismiss?.();
		}
	}, [restartAutoDismiss]);
	const handleMouseOverCapture = useCallback(() => {
		isHoveredRef.current = true;
		pauseAutoDismiss?.();
	}, [pauseAutoDismiss]);
	const handleMouseOutCapture = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			const next = event.relatedTarget as Node | null;
			if (next && event.currentTarget.contains(next)) {
				return;
			}
			isHoveredRef.current = false;
			resumeIfIdle();
		},
		[resumeIfIdle],
	);
	const handleFocusCapture = useCallback(() => {
		isFocusedRef.current = true;
		pauseAutoDismiss?.();
	}, [pauseAutoDismiss]);
	const handleBlurCapture = useCallback<React.FocusEventHandler<HTMLDivElement>>(
		(event) => {
			const next = event.relatedTarget as Node | null;
			if (next && event.currentTarget.contains(next)) {
				return;
			}
			isFocusedRef.current = false;
			resumeIfIdle();
		},
		[resumeIfIdle],
	);

	return (
		<FadeIn duration="large">
			{(props) => (
				<div
					{...props}
					onMouseOverCapture={handleMouseOverCapture}
					onMouseOutCapture={handleMouseOutCapture}
					onFocusCapture={handleFocusCapture}
					onBlurCapture={handleBlurCapture}
				>
					<Stack space="space.200">
						<Inline space="space.100" alignBlock="center" spread="space-between">
							<Box as="h2" xcss={styles.subtleHeading} ref={headingRef} tabIndex={-1}>
								{formatMessage(
									feedbackType === 'like'
										? messages.whatWorkedQuestion
										: messages.whatDidntWorkQuestion,
								)}
							</Box>
							<IconButton
								appearance="subtle"
								icon={(props) => <CrossIcon size="small" {...props} />}
								label={formatMessage(messages.dismissBanner)}
								onClick={onDismiss}
								spacing="compact"
								isTooltipDisabled={false}
							/>
						</Inline>
						<Text size="small" color="color.text.subtlest" weight="bold">
							{formatMessage(messages.itWasLabel)}
						</Text>

						<Box
							xcss={styles.categoryButtons}
							role="group"
							aria-label={formatMessage(messages.itWasLabel)}
						>
							{categories.map((category) => {
								const messageKey = CATEGORY_MESSAGE_MAP[category];
								const isSelected = selectedCategories.includes(category);
								return (
									<Button
										key={category}
										appearance={isSelected ? 'primary' : 'default'}
										onClick={() => toggleCategory(category)}
										isSelected={isSelected}
										aria-pressed={isSelected}
									>
										{formatMessage(messages[messageKey])}
									</Button>
								);
							})}
						</Box>

						<Stack space="space.100">
							<Text size="small" color="color.text.subtlest" weight="bold">
								{formatMessage(messages.additionalFeedbackLabel)}
								{isOtherSelected && (
									<>
										{' '}
										<RequiredAsterisk />
									</>
								)}
							</Text>
							<Box xcss={styles.textAreaWrap}>
								<TextArea
									ref={textAreaRef}
									appearance="none"
									placeholder={formatMessage(messages.additionalFeedbackPlaceholder)}
									value={additionalFeedback}
									onChange={(e) => {
										setAdditionalFeedback(e.target.value);
										clearValidationError();
									}}
									minimumRows={2}
									aria-label={formatMessage(messages.additionalFeedbackLabel)}
									aria-required={isOtherSelected}
									aria-invalid={showError}
									aria-describedby={showError ? errorMessageId : undefined}
								/>
							</Box>
							{showError && (
								<div id={errorMessageId}>
									<ErrorMessage>{formatMessage(messages.additionalFeedbackRequired)}</ErrorMessage>
								</div>
							)}
						</Stack>

						{!shouldHideCheckboxes && (
							<>
								<Checkbox
									label={formatMessage(messages.includeMessagesCheckbox)}
									isChecked={includeMessages}
									onChange={(e) => setIncludeMessages(e.target.checked)}
								/>
								{!isInternalUser && (
									<Checkbox
										label={
											<>
												{formatMessage(messages.enrollInResearchCheckbox, {
													privacyPolicyLink: (
														<Link href={PRIVACY_POLICY_URL} target="_blank">
															{formatMessage(messages.privacyPolicyLink)}
														</Link>
													),
												})}
											</>
										}
										isChecked={enrollInResearch}
										onChange={(e) => setEnrollInResearch(e.target.checked)}
									/>
								)}
							</>
						)}

						<Stack space="space.150">
							<Button appearance="primary" onClick={handleSubmit} shouldFitContainer>
								{formatMessage(messages.submitFeedback)}
							</Button>

							{fg('rovo_send_detailed_feedback_to_convo_ai') && (
								<Text size="small" color="color.text.subtlest">
									{formatMessage(messages.feedbackPrivacyDisclaimer)}
								</Text>
							)}
						</Stack>
					</Stack>
				</div>
			)}
		</FadeIn>
	);
};

type BannerViewState = 'opt-in-prompt' | 'opted-in' | 'thank-you' | 'initial' | 'expanded-form';

function getBannerViewState({
	isOptedIn,
	isExpanded,
	isSubmitted,
	showOptInPrompt,
}: {
	isOptedIn?: boolean;
	isExpanded: boolean;
	isSubmitted: boolean;
	showOptInPrompt: boolean;
}): BannerViewState {
	if (!isSubmitted && isExpanded) {
		return 'expanded-form';
	}
	if (showOptInPrompt) {
		return 'opt-in-prompt';
	}
	if (isOptedIn && !isExpanded) {
		return 'opted-in';
	}
	if (isSubmitted) {
		return 'thank-you';
	}
	return 'initial';
}

export const FeedbackConfirmationBanner = ({
	onProvideAdditionalFeedback,
	onDismiss,
	isVisible,
	feedbackType,
	onSubmit,
	customCategories: customCategoriesOverride,
	userEmail: userEmailOverride,
	onIncludeMessagesChecked,
	shouldHideCheckboxes: shouldHideCheckboxesOverride,
	storeUserMessages: storeUserMessagesOverride,
	onOptIn,
	isOptedIn,
	hasSeenFeedbackOptInPrompt,
	onOptInPromptActioned,
	restartAutoDismiss,
	pauseAutoDismiss,
	csatRating,
}: FeedbackConfirmationBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const {
		profileData: { email: globalEmail },
	} = useGlobalConfig();
	const { customFeedbackCategories } = useUIConfig();
	const {
		preferences: { storeUserMessages: prefsStoreUserMessages },
	} = useUserPreferences();

	const userEmail = userEmailOverride ?? globalEmail;
	const storeUserMessages = storeUserMessagesOverride ?? prefsStoreUserMessages;
	const shouldHideCheckboxes =
		shouldHideCheckboxesOverride ??
		isInternalUserWithAutoIncludeHistory(userEmail, storeUserMessages);
	const shouldShowOptIn = shouldShowOptInPrompt(userEmail);
	const shouldOfferOptInPromptByDefault =
		storeUserMessages === false && !hasSeenFeedbackOptInPrompt && !!onOptIn && shouldShowOptIn;

	const [isExpanded, setIsExpanded] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
	const [additionalFeedback, setAdditionalFeedback] = useState('');
	const [includeMessages, setIncludeMessages] = useState<boolean>(isAtlassianEmployee(userEmail));
	const [enrollInResearch, setEnrollInResearch] = useState(false);
	const [showValidationError, setShowValidationError] = useState(false);

	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const expandedFormHeadingRef = useRef<HTMLHeadingElement>(null);
	const hasEvaluatedDirectFeedbackTreatmentRef = useRef(false);

	const showOptInPrompt =
		!isOptedIn && !isExpanded && !isSubmitted && shouldOfferOptInPromptByDefault;

	useLayoutEffect(() => {
		if (!isVisible) {
			hasEvaluatedDirectFeedbackTreatmentRef.current = false;
			return;
		}
		if (csatRating != null && !isExpanded && !isSubmitted) {
			setIsExpanded(true);
			return;
		}
		const isAtTreatmentDecisionPoint =
			!isSubmitted && !isExpanded && !isOptedIn && !shouldOfferOptInPromptByDefault;
		if (!isAtTreatmentDecisionPoint || hasEvaluatedDirectFeedbackTreatmentRef.current) {
			return;
		}

		hasEvaluatedDirectFeedbackTreatmentRef.current = true;
		setIsExpanded(true);
	}, [isVisible, isSubmitted, isExpanded, isOptedIn, shouldOfferOptInPromptByDefault, csatRating]);

	const bannerView = getBannerViewState({
		isOptedIn,
		isExpanded,
		isSubmitted,
		showOptInPrompt: !!showOptInPrompt,
	});

	const handleProvideAdditionalFeedback = useCallback(() => {
		setIsExpanded(true);
		onProvideAdditionalFeedback();
	}, [onProvideAdditionalFeedback]);

	useLayoutEffect(() => {
		if (isExpanded) {
			expandedFormHeadingRef.current?.focus();
		}
	}, [isExpanded]);

	const handleOptInWithMarkSeen = useCallback(() => {
		onOptInPromptActioned?.();
		onOptIn?.();
	}, [onOptInPromptActioned, onOptIn]);

	const handleDismissWithMarkSeen = useCallback(() => {
		onOptInPromptActioned?.();
		onDismiss();
	}, [onOptInPromptActioned, onDismiss]);

	const handleAdditionalFeedbackWithMarkSeen = useCallback(() => {
		onOptInPromptActioned?.();
		handleProvideAdditionalFeedback();
	}, [onOptInPromptActioned, handleProvideAdditionalFeedback]);

	const categories = React.useMemo((): readonly string[] => {
		if (customCategoriesOverride) {
			return customCategoriesOverride;
		}
		if (customFeedbackCategories?.positive && customFeedbackCategories?.negative) {
			return (
				feedbackType === 'like'
					? customFeedbackCategories.positive
					: customFeedbackCategories.negative
			) as readonly string[];
		}
		return feedbackType === 'like' ? POSITIVE_CATEGORIES : NEGATIVE_CATEGORIES;
	}, [
		feedbackType,
		customCategoriesOverride,
		customFeedbackCategories?.positive,
		customFeedbackCategories?.negative,
	]);

	const toggleCategory = useCallback((category: string) => {
		setSelectedCategories((prev) =>
			prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
		);
		setShowValidationError(false);
	}, []);

	const clearValidationError = useCallback(() => {
		setShowValidationError(false);
	}, []);

	const handleSubmit = useCallback(() => {
		if (selectedCategories.includes('other') && !additionalFeedback.trim()) {
			setShowValidationError(true);
			textAreaRef.current?.focus();
			return;
		}

		// Fire analytic if includeMessages checkbox is checked when submitting
		if (includeMessages && onIncludeMessagesChecked) {
			onIncludeMessagesChecked();
		}

		onSubmit({
			categories: selectedCategories,
			additionalFeedback,
			includeMessages,
			enrollInResearch,
		});
		setIsSubmitted(true);
		setIsExpanded(false);
	}, [
		selectedCategories,
		additionalFeedback,
		includeMessages,
		enrollInResearch,
		onSubmit,
		onIncludeMessagesChecked,
	]);

	return (
		<>
			<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
				{isSubmitted && formatMessage(messages.thankYouMessage)}
			</VisuallyHidden>
			<ExitingPersistence>
				{isVisible && (
					<FadeIn exitDirection="top" duration="large">
						{(motionProps) => (
							<div {...motionProps}>
								<Stack space="space.200" xcss={styles.bannerContainer}>
									{bannerView === 'opted-in' && (
										<OptedInMessage
											onDismiss={onDismiss}
											onAdditionalFeedback={handleProvideAdditionalFeedback}
										/>
									)}

									{bannerView === 'opt-in-prompt' && (
										<ThankYouOptInPrompt
											onDismiss={handleDismissWithMarkSeen}
											onOptIn={handleOptInWithMarkSeen}
											onAdditionalFeedback={handleAdditionalFeedbackWithMarkSeen}
										/>
									)}

									{(bannerView === 'thank-you' || bannerView === 'initial') && (
										<ThankYouMessage onDismiss={onDismiss} />
									)}

									{bannerView === 'initial' && (
										<Button onClick={handleProvideAdditionalFeedback} shouldFitContainer>
											{formatMessage(messages.provideAdditionalFeedback)}
										</Button>
									)}

									{bannerView === 'expanded-form' && (
										<ExpandedFeedbackForm
											feedbackType={feedbackType}
											onDismiss={onDismiss}
											categories={categories}
											selectedCategories={selectedCategories}
											toggleCategory={toggleCategory}
											additionalFeedback={additionalFeedback}
											setAdditionalFeedback={setAdditionalFeedback}
											includeMessages={includeMessages}
											setIncludeMessages={setIncludeMessages}
											enrollInResearch={enrollInResearch}
											setEnrollInResearch={setEnrollInResearch}
											handleSubmit={handleSubmit}
											showValidationError={showValidationError}
											clearValidationError={clearValidationError}
											shouldHideCheckboxes={shouldHideCheckboxes ?? false}
											isInternalUser={shouldShowOptIn}
											textAreaRef={textAreaRef}
											headingRef={expandedFormHeadingRef}
											restartAutoDismiss={restartAutoDismiss}
											pauseAutoDismiss={pauseAutoDismiss}
										/>
									)}
								</Stack>
							</div>
						)}
					</FadeIn>
				)}
			</ExitingPersistence>
		</>
	);
};
